import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../../services/api';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import LocationForm from './LocationForm';

const LocationsPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const queryClient = useQueryClient();

  // Fetch locations data with React Query
  const {
    data: locationsData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(
    ['locations'],
    async () => {
      try {
        console.log('Fetching locations for management table...');
        const response = await api.get('/api/v1/admin/locations?paginate=true');
        console.log('Locations management API response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching locations:', error);
        throw new Error('Failed to fetch locations. Please try again later.');
      }
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchInterval: 30000, // Refetch every 30 seconds
      staleTime: 10000 // Consider data stale after 10 seconds
    }
  );

  // Delete location mutation
  const deleteLocationMutation = useMutation(
    async (locationId) => {
      return await api.delete(`/api/v1/admin/locations/${locationId}`);
    },
    {
      onSuccess: () => {
        toast.success('Location deleted successfully');
        queryClient.invalidateQueries('locations');
        setShowDeleteModal(false);
        setLocationToDelete(null);
      },
      onError: (error) => {
        console.error('Error deleting location:', error);
        toast.error('Failed to delete location. It may be in use by tours.');
      }
    }
  );

  const handleAddLocation = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleDeleteLocation = (location) => {
    setLocationToDelete(location);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (locationToDelete) {
      deleteLocationMutation.mutate(locationToDelete.id);
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingLocation(null);
    refetch();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingLocation(null);
  };

  // Extract locations from the API response, handling all possible formats
  const extractLocations = (data) => {
    console.log('Extracting locations from:', data);
    
    // If data is directly an array
    if (Array.isArray(data)) {
      console.log('Data is directly an array');
      return data;
    }
    
    // If data has a 'data' property that is an array
    if (data?.data && Array.isArray(data.data)) {
      console.log('Data has a data property that is an array');
      return data.data;
    }
    
    // If data has a paginated format
    if (data?.data?.data && Array.isArray(data.data.data)) {
      console.log('Data has a paginated format');
      return data.data.data;
    }
    
    // If nothing matches, return empty array
    console.warn('Could not extract locations from data:', data);
    return [];
  };
  
  const locations = extractLocations(locationsData);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Locations Management</h1>
        <button
          onClick={handleAddLocation}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Add Location
        </button>
      </div>

      {isError && (
        <div className="mb-6 p-4 bg-red-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading locations
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error?.message || 'An unexpected error occurred. Please try again.'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm ? (
        <LocationForm 
          location={editingLocation} 
          onSubmit={handleFormSubmit} 
          onCancel={handleFormCancel} 
        />
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {locations.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500 mb-4">No locations found. Create your first location to get started.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {locations.map((location) => (
                <li key={location.id}>
                  <div className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{location.name}</h3>
                      <p className="text-sm text-gray-500">{location.region || 'No region specified'}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditLocation(location)}
                        className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteLocation(location)}
                        className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Location</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the location "{locationToDelete?.name}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsPage;
